/**
 * components/BetaApplication/steps/BetaCommitmentStep.tsx
 * Beta Commitment Step - Step 4 of Beta Application
 *
 * Collects beta testing commitment information:
 * - Interest in journaling features
 * - Testing commitment level
 * - Feedback commitment willingness
 */

// 1. External dependencies
import React, { useState, useMemo, useEffect } from 'react';

// 2. Internal absolute imports
import { Form, Select, SelectItem, TextArea, Card, CardHeader, CardBody } from '@enfomentis/ui';
import { Brand } from '@enfomentis/shared/config';
import { useBetaApplicationData } from '../hooks/useBetaApplicationData';
import {
  validateCommitmentStep,
  validateRequiredSelection,
  validateTextField
} from '../utils/betaApplicationValidation';
import {
  TestingCommitmentEnum,
  FeedbackCommitmentEnum,
  type TestingCommitment,
  type FeedbackCommitment
} from '../types/BetaApplicationTypes';

/**
 * Beta Commitment Step Component
 *
 * Complete functional form for collecting beta testing commitment
 * with real-time validation.
 */
export const BetaCommitmentStep: React.FC = () => {
  const { data: draft, updateData } = useBetaApplicationData();

  // Field touched states for validation timing
  const [stjInterestTouched, setStjInterestTouched] = useState(false);
  const [testingCommitmentTouched, setTestingCommitmentTouched] = useState(false);
  const [feedbackCommitmentTouched, setFeedbackCommitmentTouched] = useState(false);

  // Local validation functions for HeroUI components
  const validateStjInterestField = (value: string): string | null => {
    const result = validateTextField(value, `Interest in ${Brand.displayName} journaling features`, 500);
    return result.isValid ? null : (result.error || null);
  };

  const validateTestingCommitmentField = (value: string): string | null => {
    const result = validateRequiredSelection(value, "Testing commitment");
    return result.isValid ? null : (result.error || null);
  };

  const validateFeedbackCommitmentField = (value: string): string | null => {
    const result = validateRequiredSelection(value, "Feedback commitment");
    return result.isValid ? null : (result.error || null);
  };

  // Real-time validation - only show errors after field is touched
  const stjInterestError = useMemo(() => {
    if (!stjInterestTouched || !draft) return null;
    return validateStjInterestField(draft.commitment.stjInterest);
  }, [stjInterestTouched, draft]);

  const testingCommitmentError = useMemo(() => {
    if (!testingCommitmentTouched || !draft) return null;
    return validateTestingCommitmentField(draft.commitment.testingCommitment);
  }, [testingCommitmentTouched, draft]);

  const feedbackCommitmentError = useMemo(() => {
    if (!feedbackCommitmentTouched || !draft) return null;
    return validateFeedbackCommitmentField(draft.commitment.feedbackCommitment);
  }, [feedbackCommitmentTouched, draft]);

  // Update step completion status when validation changes
  useEffect(() => {
    if (!draft) return;

    const isStepValid = validateCommitmentStep(draft).isValid;
    const isCompleted = draft.completedSteps.includes('commitment');

    if (isStepValid && !isCompleted) {
      updateData((current) => ({
        ...current,
        completedSteps: [...current.completedSteps, 'commitment']
      }));
    } else if (!isStepValid && isCompleted) {
      updateData((current) => ({
        ...current,
        completedSteps: current.completedSteps.filter(step => step !== 'commitment')
      }));
    }
  }, [draft, updateData]);

  // Form submission handler (for enter key behavior)
  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    // Form submission is handled by WizardLayout navigation
  };

  // Field change handlers
  const handleStjInterestChange = (value: string): void => {
    updateData((current) => ({
      ...current,
      commitment: { ...current.commitment, stjInterest: value },
      lastModified: new Date().toISOString()
    }));
  };

  const handleTestingCommitmentChange = (value: TestingCommitment | ''): void => {
    updateData((current) => ({
      ...current,
      commitment: { ...current.commitment, testingCommitment: value },
      lastModified: new Date().toISOString()
    }));
  };

  const handleFeedbackCommitmentChange = (value: FeedbackCommitment | ''): void => {
    updateData((current) => ({
      ...current,
      commitment: { ...current.commitment, feedbackCommitment: value },
      lastModified: new Date().toISOString()
    }));
  };

  if (!draft) {
    return <div>Loading...</div>;
  }

  // Show commitment form
  return (
    <Card
      radius='none'
      shadow='none'
      className="max-w-2xl mx-auto"
    >
      <CardHeader className="text-2xl font-semibold mb-2">
          Beta Commitment
      </CardHeader>

      <CardBody>
        <Form
          validationBehavior="aria"
          onSubmit={handleSubmit}
          className="space-y-6"
        >
          {/* Interest TextArea */}
          <TextArea
            label={`Interest in ${Brand.displayName} Journaling Features`}
            labelPlacement="outside-top"
            placeholder={`Tell us about your interest in ${Brand.displayName}'s journaling features...`}
            description={`What interests you most about ${Brand.displayName}'s approach to trading psychology and journaling?`}
            value={draft.commitment.stjInterest}
            onValueChange={handleStjInterestChange}
            onBlur={() => setStjInterestTouched(true)}
            validate={validateStjInterestField}
            isRequired
            minRows={3}
            maxRows={6}
            errorMessage={stjInterestError}
            isInvalid={!!stjInterestError}
            classNames={{
              input: "text-base pt-2",
            }}
          />

          {/* Testing Commitment Selection */}
          <Select
            label="Testing Commitment"
            labelPlacement="outside"
            placeholder="Select your testing commitment level"
            description="Can you commit to actively testing new features and providing feedback?"
            selectedKeys={draft.commitment.testingCommitment ? [draft.commitment.testingCommitment] : []}
            onSelectionChange={(keys) => {
              const value = Array.from(keys)[0] as TestingCommitment | undefined;
              handleTestingCommitmentChange(value || '');
            }}
            onClose={() => setTestingCommitmentTouched(true)}
            isRequired
            errorMessage={testingCommitmentError}
            isInvalid={!!testingCommitmentError}
            classNames={{
              trigger: "text-base",
            }}
          >
            <SelectItem key={TestingCommitmentEnum.YesCommitted}>
              Yes, I can commit to regular testing
            </SelectItem>
            <SelectItem key={TestingCommitmentEnum.MaybeLimited}>
              Maybe, depending on my schedule
            </SelectItem>
            <SelectItem key={TestingCommitmentEnum.NoLimited}>
              No, I have limited availability
            </SelectItem>
          </Select>

          {/* Feedback Commitment Selection */}
          <Select
            label="Feedback Commitment"
            labelPlacement="outside"
            placeholder="Select your feedback commitment level"
            description="Are you willing to provide feedback and suggestions for improvement?"
            selectedKeys={draft.commitment.feedbackCommitment ? [draft.commitment.feedbackCommitment] : []}
            onSelectionChange={(keys) => {
              const value = Array.from(keys)[0] as FeedbackCommitment | undefined;
              handleFeedbackCommitmentChange(value || '');
            }}
            onClose={() => setFeedbackCommitmentTouched(true)}
            isRequired
            errorMessage={feedbackCommitmentError}
            isInvalid={!!feedbackCommitmentError}
            classNames={{
              trigger: "text-base",
            }}
          >
            <SelectItem key={FeedbackCommitmentEnum.YesCommitted}>
              Yes, I'm committed to providing regular feedback
            </SelectItem>
            <SelectItem key={FeedbackCommitmentEnum.YesLimited}>
              Yes, but my feedback may be limited
            </SelectItem>
            <SelectItem key={FeedbackCommitmentEnum.NoMinimal}>
              No, I prefer minimal feedback requirements
            </SelectItem>
          </Select>

        </Form>
      </CardBody>
    </Card>
  );
};